if ( typeof ProductVariants !== 'function' ) {

    class ProductVariants extends HTMLElement {

        constructor() {

            super();
            this._cachedOptions = new Map(); // Map for cached options with LRU eviction
            this._loadingOptions = new Set(); // Options currently being loaded
            this._debounceTimers = {}; // Debounce timers for mouseover events
            this._prefetchedUrls = new Set(); // Set to keep track of prefetched URLs
            this.observer = null; // IntersectionObserver instance
            this.CACHE_LIMIT = 100; // Cache limit
            this._prefix = window.KT_PREFIX || '';
            this._listenerController = null;

            this.init();

        }

        init() {

            if ( this.hasAttribute('data-enable-skeleton' ) && this.closest(`.${this._prefix}has-skeleton`) ) {
                const skeleton = this.closest(`.${this._prefix}has-skeleton`).querySelector(`[data-js-product-item-skeleton]`);
                this.skeletonElements = [...skeleton.querySelector(`.${this._prefix}product-item__text`).children];
                this.skeletonSettings = JSON.parse(skeleton.querySelector('[data-product-skeleton-settings]').textContent);
            }

            this._event = new Event('VARIANT_CHANGE');

            this.productPage = this.closest('[data-js-product-component]');

            this.productForm = document.querySelector(`#product-form-${this.dataset.id}`);
            if ( this.productForm ) {
                this.productQty = this.productForm.querySelector('[data-js-product-quantity]');
                this.addToCart = this.productForm.querySelector('[data-js-product-add-to-cart]');
                this.addToCartText = this.productForm.querySelector('[data-js-product-add-to-cart-text]');
            }

            this.addEventListener('change', this.#onOptionChange.bind(this));
            let initVariantChange = false;

            if ( this.hasAttribute('data-variant-required' ) ) {
                this.variantRequired = true;
                this.noVariantSelectedYet = true;
                this.fauxVariant = JSON.parse(this.querySelector('[data-js-variant-data]').textContent);
                // Was previously gated on document.location.search.includes('variant')
                // rather than on initVariant actually being valid - so a bad ?variant=
                // value (eg. the literal string "undefined", see #updateOptions()'s
                // data-variant-id guard above) still took this branch, found
                // parseInt(...) was NaN, ran nothing inside, and never fell through
                // to the else branch below - permanently stranding variantRequired/
                // noVariantSelectedYet true with no path back to false. Checking
                // initVariant's validity directly here means any invalid value now
                // correctly falls through to the normal init path instead.
                const initVariant = this.hasAttribute('data-main-product-page-variants')
                    ? parseInt(new URLSearchParams(document.location.search).get('variant'))
                    : NaN;
                if ( initVariant ) {
                        this.#updateCurrentVariant();
                        this.variantRequired = false;
                        this.noVariantSelectedYet = false;
                        initVariantChange = true;
                        this.classList.add(`${this._prefix}variant-selected`);
                        this.productPage.classList.add(`${this._prefix}variant-selected`);
                        this.querySelectorAll('[data-option-value-id][data-selected]').forEach(elm=>{
                            elm.tagName === 'OPTION' ? elm.setAttribute('selected', 'selected') : elm.setAttribute('checked', 'checked');
                        });
                } else {
                    
                    if ( this.getAttribute('data-unavailable-variants') !== 'hide' ) {
                        this.querySelectorAll('[disabled]').forEach(elm=>{
                            if ( ! elm.hasAttribute('data-disabled') ) {
                                elm.removeAttribute('disabled')
                            }
                        });
                        this.querySelectorAll(`.${this._prefix}disabled`).forEach(elm=>{
                            elm.classList.remove(`${this._prefix}disabled`)
                        });
                    }

                    this.querySelectorAll('[data-silent-selected]').forEach(elm=>{
                        if ( elm.dataset.productUrl == this.dataset.url ) {
                            elm.removeAttribute('data-silent-selected');
                            elm.tagName === 'OPTION' ? elm.setAttribute('selected', 'selected') : elm.setAttribute('checked', 'checked');
                            if ( parseInt(this.dataset.variants) == 1 ) {
                                this.variantRequired = false;
                                this.noVariantSelectedYet = false;
                                this.classList.add(`${this._prefix}variant-selected`);
                                this.productPage.classList.add(`${this._prefix}variant-selected`);
                                initVariantChange = true;
                            }
                            this.#checkIfSelectedVariant();
                            this.#updateCurrentVariant();
                        }
                    });
                    const combination = [];
                    this.querySelectorAll('[data-option-value-id][data-selected]').forEach(elm=>{
                        combination.push(elm.dataset.optionValueId);
                    });
                    this.#debounceOptionLoad(combination.join(',').replace(/,/g, '-'), 1);
                }
            } else {
                this.variantRequired = false;
                this.noVariantSelectedYet = false;
                this.classList.add(`${this._prefix}variant-selected`);
                this.productPage.classList.add(`${this._prefix}variant-selected`);
                this.#updateCurrentVariant();
            }

            this.productStock = document.querySelector(`#product-${this.dataset.id} [data-js-variant-quantity]`);
            this.productStockProgress = document.querySelector(`#product-${this.dataset.id} [data-js-variant-quantity-progress]`);
            if ( this.productStock && document.querySelector(`#product-${this.dataset.id} [data-js-variant-quantity-data]`) ) {
                this.updateStock(JSON.parse(document.querySelector(`#product-${this.dataset.id} [data-js-variant-quantity-data]`).getAttribute('data-inventory')));
            }

            if ( initVariantChange ) {
                const combination = [];
                this.querySelectorAll('[data-option-value-id][data-selected]').forEach(elm => {
                    combination.push(elm.dataset.optionValueId);
                });
                this.#fetchProductUpdates(combination.join(',').replace(/,/g, '-'), this.#renderProductUpdates.bind(this), false, true);
            }

            // preloading of option values

            new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const combinations = this.#getPossibleCombinations();
                        combinations.forEach((combination) => {
                            this.#debounceOptionLoad(combination.join(',').replace(/,/g, '-'));
                        });
                        this.querySelectorAll('[data-product-url]').forEach(elm=>{
                            this.#prefetchPage(elm.dataset.productUrl);
                        })
                    }
                });
            }, {
                rootMargin: '0px',
                threshold: 0.1,
            }).observe(this);

            new MutationObserver(() => {
                const combinations = this.#getPossibleCombinations();
                combinations.forEach((combination) => {
                    this.#debounceOptionLoad(combination.join(',').replace(/,/g, '-'));
                });
                this.querySelectorAll('[data-product-url]').forEach(elm=>{
                    this.#prefetchPage(elm.dataset.productUrl);
                })
            }).observe(this.querySelector('[data-js-variant-data]'), {
                attributes: false, childList: true, subtree: false
            });

            this.#addEventListeners();

        }

        #addEventListeners() {

            this.querySelectorAll('[data-option-value-id]').forEach((elm) => {

                if ( elm._hasPreloadListeners ) return;
                elm._hasPreloadListeners = true;

                elm.addEventListener('mouseover', (e) => {
                    e.stopPropagation();
                    if ( ! e.target.hasAttribute('data-selected') && ! e.target.hasAttribute('data-product-url') ) {
                        const combination = this.#getSpecificCombinations(e.target, e.target.closest('[data-js-product-variant-container]'));
                        this.#debounceOptionLoad(combination.join(',').replace(/,/g, '-'), 1);
                    } else if ( e.target.hasAttribute('data-custom-product-url' ) ) {
                        const combination = this.#getSpecificCombinations(e.target, e.target.closest('[data-js-product-variant-container]'));
                        this.#debounceOptionLoad(combination.join(',').replace(/,/g, '-'), 1, e.target.getAttribute('data-custom-product-url'));
                    }
                });

                elm.addEventListener('touchstart', e => {
                    e.stopPropagation();
                    if ( ! e.target.hasAttribute('data-selected') && ! e.target.hasAttribute('data-product-url') ) {
                        const combination = this.#getSpecificCombinations(e.target, e.target.closest('[data-js-product-variant-container]'));
                        this.#debounceOptionLoad(combination.join(',').replace(/,/g, '-'), 1);
                    } else if ( e.target.hasAttribute('data-custom-product-url' ) ) {
                        const combination = this.#getSpecificCombinations(e.target, e.target.closest('[data-js-product-variant-container]'));
                        this.#debounceOptionLoad(combination.join(',').replace(/,/g, '-'), 1, e.target.getAttribute('data-custom-product-url'));
                    }
                }, { passive:true });

            });

        }

        #debounceOptionLoad(optionValuesHash, delay = 20) {
            // Debounce function to prevent multiple quick fetches
            if (this._debounceTimers[optionValuesHash]) {
                clearTimeout(this._debounceTimers[optionValuesHash]);
            }
            this._debounceTimers[optionValuesHash] = setTimeout(() => {
                if (!this._cachedOptions.has(optionValuesHash) && !this._loadingOptions.has(optionValuesHash)) {
                    this.#preloadOption(optionValuesHash);
                }
            }, delay);
        }

        #preloadOption(optionValuesHash) {
            // Preload a single option
            this._loadingOptions.add(optionValuesHash);
            this.#fetchProductUpdates(optionValuesHash, (html) => {
                this.#cacheProductUpdates(html, optionValuesHash);
                this._loadingOptions.delete(optionValuesHash);
            });
        }
    
        #prefetchPage(url) {
            // Prefetch the page using a link element
            if ( ! this._prefetchedUrls.has(url) && url !== window.location.pathname ) {
                const link = document.createElement('link');
                link.rel = 'prefetch';
                link.href = url;
                document.head.appendChild(link);
                this._prefetchedUrls.add(url);
            }
        }

        #getSelectedOptionHash(){
            return [...this.querySelectorAll('select[data-js-product-variant-container], input:checked')]
                .map(el => el.dataset.optionValueId || el.selectedOptions[0]?.dataset.optionValueId)
                .filter(value => value !== undefined)
                .join(',').replace(/,/g, '-');
        }

        #getPossibleCombinations() {
    
            const currentSelections = [];
            const options = [];
    
            this.querySelectorAll('[data-js-product-variant-container]').forEach(row => {
                const rowOptions = [];
                let currentSelection = null;

                row.querySelectorAll('[data-option-value-id]').forEach(option => {
                    rowOptions.push(option.getAttribute('data-option-value-id'));
                    if (option.hasAttribute('data-selected')) {
                        currentSelection = option.getAttribute('data-option-value-id');
                    }
                });
        
                if (currentSelection === null) {
                    //console.error('No selected option found in one of the rows');
                    return;
                }
        
                options.push(rowOptions);
                currentSelections.push(currentSelection);
            });
        
            function generatePossibleCombinations(currentSelections, options) {
                const possibleCombinations = [];
        
                for (let i = 0; i < currentSelections.length; i++) {
                    const newCombination = [...currentSelections];
                    options[i].forEach((option) => {
                        if ( option !== currentSelections[i] && ! this.querySelector(`[data-option-value-id="${option}"]`).hasAttribute('data-product-url') ) {
                            newCombination[i] = option;
                            possibleCombinations.push([...newCombination]);
                        }
                    });
                }
                return possibleCombinations;
            }
        
            return generatePossibleCombinations.call(this, currentSelections, options);
        }

        #getSpecificCombinations(hoveredOption, hoveredRow) {
            const newCombination = [];
            this.querySelectorAll('[data-js-product-variant-container]').forEach(row => {
                if ( row === hoveredRow ) {
                    newCombination.push(hoveredOption.dataset.optionValueId);
                } else {
                    newCombination.push(row.querySelector('[data-selected]').dataset.optionValueId);
                }
            });
            return newCombination;
        }

        async #fetchProductUpdates(optionValuesHash, callback, checkHash = false, force = false, priority = 'auto') {
            // turn from hash to no hash
            // Fetch product updates
            try {
                const response = await fetch(`${this.dataset.url}${this.dataset.url.includes('?') ? '&' : '?'}section_id=${this.dataset.helperId}&option_values=${optionValuesHash.replace(/-/g, ',')}`, {priority: priority});
                const responseText = await response.text();
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                if ( optionValuesHash === this.currentOptionValueHash || checkHash === false || force ) {
                    callback(html, optionValuesHash);
                }
            } catch (error) {
                console.error(`Error fetching options ${optionValuesHash}:`, error);
                this._loadingOptions.delete(optionValuesHash);
            }
        }

        async #fetchAndReloadProductItem(url, productItem) {

            productItem.classList.add('loading');

            try {

                const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=helper-product-item`);   
                let responseText = await response.text();
                const responseId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                responseText = responseText.replaceAll(/UNIQUE_ID/g, responseId);

                const html = new DOMParser().parseFromString(responseText, 'text/html');
                KROWN.productItemUpdateHelper(productItem, responseId, html, {elements: this.skeletonElements, settings: this.skeletonSettings});
                productItem.classList.remove('loading');

                productItem.dispatchEvent(new Event('reload', { bubbles: true }));

            } catch (error) {
                console.error(`Error`, error); 
                productItem.classList.remove('loading');
            }
        }       
        
        async #fetchAndReloadProductPage(url, productPage) {

            productPage.classList.add('loading');

            try {
                const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}view=quick-view`);  
                let responseText = await response.text();
                const quickViewHTML = new DOMParser().parseFromString(responseText, 'text/html').querySelector('#product-quick-view');
                productPage.innerHTML = quickViewHTML.innerHTML;
                productPage.classList.remove('loading');
                productPage.dispatchEvent(new Event('reload', { bubbles: true }));
            } catch (error) {
                console.error(`Error`, error); 
                productPage.classList.remove('loading');
            }
        }

        #cacheProductUpdates(html, optionValuesHash) {
            // Cache the fetched HTML with LRU eviction
            if (this._cachedOptions.size >= this.CACHE_LIMIT) {
                // Remove the oldest item (first entry in the Map)
                const oldestKey = this._cachedOptions.keys().next().value;
                this._cachedOptions.delete(oldestKey);
            }
    
            // Add the new item
            this._cachedOptions.set(optionValuesHash, html);
            // Ensure the most recently accessed item is moved to the end of the Map
            this.#updateCacheOrder(optionValuesHash);
        }

        #updateCacheOrder(optionValuesHash) {
            // Move the accessed item to the end to mark it as recently used
            const value = this._cachedOptions.get(optionValuesHash);
            this._cachedOptions.delete(optionValuesHash);
            this._cachedOptions.set(optionValuesHash, value);
        }

        // The product a combined-listing swatch leads to (a wood type, say) is in no
        // collection, so it can't work out a back-link of its own. Leave this page's
        // for it: snippets/breadcrumb.liquid restores it there, by URL, from sessionStorage.
        #rememberBackLink(targetPath) {
            const link = document.querySelector('.breadcrumb-back-link');
            if ( ! link ) {
                return;
            }
            try {
                sessionStorage.setItem('pdp-back-link', JSON.stringify({
                    target: targetPath,
                    href: link.getAttribute('href'),
                    label: link.textContent.trim(),
                    at: Date.now()
                }));
            } catch(e) {}
        }

        // Same hand-off as #rememberBackLink above, for whether the shopper
        // had already opened the 3D/AR view before this navigation - a
        // combined-listing swatch (Wood Type) leads to a genuinely
        // different product/page, so component-product-ar-launcher.js's own
        // in-page state can't survive it either. See maybeAutoEngage() in
        // that file, which auto-opens the 3D view on the destination page
        // instead of making them click "3D" again for a wood type they were
        // already looking at in 3D a moment ago.
        #remember3DEngaged(targetPath) {
            const photo = document.querySelector('[data-ar-photo]');
            if ( ! photo || ! photo.hidden ) {
                return;
            }
            try {
                sessionStorage.setItem('pdp-3d-engaged', JSON.stringify({
                    target: targetPath,
                    at: Date.now()
                }));
            } catch(e) {}
        }

        // Same hand-off again, so section-main-product.js can put the gallery
        // at the top of the screen on the page a swatch navigates to (the
        // new page would otherwise land wherever the browser's own scroll
        // restoration / the #anchor below leaves it, which on mobile left
        // the product info in view and the header popping back in). Returns
        // whether it was stored - without it, the #anchor stays as the
        // fallback.
        #rememberGalleryScroll(targetPath) {
            try {
                sessionStorage.setItem('pdp-scroll-gallery', JSON.stringify({
                    target: targetPath,
                    at: Date.now()
                }));
                return true;
            } catch(e) {
                return false;
            }
        }

        #onOptionChange(e) {
            const variant = e.target;
            const targetUrl = variant instanceof HTMLSelectElement ? variant.selectedOptions[0].dataset.productUrl : variant.dataset.productUrl;
            const variantId = variant instanceof HTMLSelectElement ? variant.selectedOptions[0].dataset.variantId : variant.dataset.variantId
            const optionValuesHash = this.#getSelectedOptionHash();
            this.currentOptionValueHash = optionValuesHash;
            this.#addEventListeners();

            if ( targetUrl !== '' && targetUrl !== undefined && targetUrl !== this.dataset.url ) {
                if ( variant.hasAttribute('data-product-custom-url') || ( variant instanceof HTMLSelectElement && variant.selectedOptions[0].hasAttribute('data-product-custom-url') ) ) {
                    if ( variant.dataset.productCustomUrl == 'product' ) {
                        this.#fetchAndReloadProductItem(targetUrl, variant.closest('[data-js-product-item]'));
                    } else {
                        this.#fetchAndReloadProductPage(targetUrl, variant.closest('[data-js-product-page]'));
                    }
                } else {
                    const productComponent = this.closest('[data-js-product-component]') || document.querySelector('[data-js-product-component]');
                    const anchor = productComponent && productComponent.id ? `#${productComponent.id}` : '';

                    // A combined-listing swatch (e.g. Wood Type) navigates to a
                    // genuinely different product, so none of this component's
                    // in-page state survives. component-pdp-drawers.js's own
                    // ?upsell= deep link + restoreFromURL() already knows how to
                    // re-select a Performance Tech/Accessory pick (and re-sync the
                    // hidden variant-option radio) from a URL alone — carrying that
                    // same param across this navigation is what makes the drawer
                    // selection (and its price surcharge) survive a wood/material
                    // change instead of silently resetting on the new product page.
                    const destination = new URL(targetUrl, window.location.origin);
                    const currentUpsell = new URLSearchParams(window.location.search).get('upsell');
                    if (currentUpsell) {
                        destination.searchParams.set('upsell', currentUpsell);
                    }
                    this.#rememberBackLink(destination.pathname);
                    this.#remember3DEngaged(destination.pathname);
                    const scrollsToGallery = this.#rememberGalleryScroll(destination.pathname);
                    window.location.assign(`${destination.pathname}${destination.search}${scrollsToGallery ? '' : anchor}`);
                }

            } else {

                if ( this.variantRequired && this.noVariantSelectedYet ) {
                    this.#checkIfSelectedVariant();
                }
                
                if ( this.noVariantSelectedYet == false ) {
                    if (this._cachedOptions.has(optionValuesHash)) {
                        // Use cached content if available
                        this.#renderProductUpdates(this._cachedOptions.get(optionValuesHash));
                        this.#updateCacheOrder(optionValuesHash);
                    } else {
                        // Fetch updates if not cached
                        this.#fetchProductUpdates(optionValuesHash, this.#renderProductUpdates.bind(this), true, false, 'high');
                    }
                    this.#updateURL(variantId);

                }

            }

        }

        #renderProductUpdates(html) {

            const newHTML = html.querySelector(`#product-${this.hasAttribute('data-render-with-helper') ? this.dataset.helperId : this.dataset.id}`);

            // update variant specific blocks
            
            newHTML.querySelectorAll('[data-update-block]').forEach(elm=>{
                if ( ! elm.hasAttribute('data-update-block-inner') ) {
                    const target = this.productPage.querySelector(`[data-update-block="${elm.dataset.updateBlock}"]`);
                    if ( target ) {
                        target.innerHTML = elm.innerHTML;
                    }
                }
            });

            // update product id (selected variant)
            const newVariantInput = newHTML.querySelector('input[name="id"]');
            if ( newVariantInput ) {
                this.productPage.querySelectorAll('input[name="id"]').forEach(elm=>{
                    if ( elm.closest('[data-js-product-component]') === this.productPage ) {
                        elm.value = newVariantInput.value;
                    }
                });
            }
            
            // update current variant and available options
            this.#updateCurrentVariant();
            this.#updateOptions(newHTML.querySelectorAll('[data-main-product-variants] [data-option-value-id]'));

            // handle stock related updates
            if ( this.productStock && newHTML.querySelector('[data-js-variant-quantity-data]' ) ) {
                this.updateStock(JSON.parse(newHTML.querySelector('[data-js-variant-quantity-data]').getAttribute('data-inventory')));
            }
            this.#updateOutOfStockVariants();
            this.updateBuyButtons();
        
            this.dispatchEvent(this._event);
            KROWN.functions.eventDispatcher('krown:variant:change', {
                variant: this.currentVariant,
                productForm: this.productForm
            });

        }

        #updateOptions(optionsData) {

            optionsData.forEach(option => {

                const optionElm = this.querySelector(`[data-option-value-id="${option.dataset.optionValueId}"]`);

                optionElm.setAttribute('data-available', option.dataset.available);
                // Hidden options (sections/main-product.liquid's hidden_option_names,
                // eg. Performance Technology) never carry a real data-variant-id -
                // that attribute is only rendered for visible pickers (snippets/
                // product-variant-options.liquid). setAttribute coerces its value
                // via ToString, so passing the resulting `undefined` here wrote the
                // literal string "undefined" onto the element - which #onOptionChange
                // later read back as variantId and wrote into the URL as a real
                // ?variant=undefined query param. That persisted across reloads and
                // made init() wrongly take the "restore from ?variant=" branch
                // (parseInt("undefined") is NaN, so nothing inside ever ran),
                // permanently skipping the normal noVariantSelectedYet setup -
                // silently breaking every later real variant swap (price stayed
                // stuck on a stale base while the drawer's own surcharge kept
                // stacking on top) for the rest of that page's life. Confirmed live
                // 2026-09-09 from a shopper's own recording. Only set the attribute
                // when there's a real value; otherwise remove it so a stale one from
                // an earlier render can't linger either.
                if ( option.dataset.variantId ) {
                    optionElm.setAttribute('data-variant-id', option.dataset.variantId);
                } else {
                    optionElm.removeAttribute('data-variant-id');
                }
                if ( option.hasAttribute('data-product-url') ) {
                    optionElm.setAttribute('data-product-url', option.dataset.productUrl);
                } else {
                    optionElm.removeAttribute('data-product-url');
                }
                if ( option.hasAttribute('data-selected') ) {
                    optionElm.setAttribute('data-selected', '');
                    optionElm.setAttribute(`${optionElm instanceof HTMLOptionElement ? 'selected' : 'checked'}`, '');
                } else {
                    optionElm.removeAttribute('data-selected');
                    optionElm.removeAttribute(`${optionElm instanceof HTMLOptionElement ? 'selected' : 'checked'}`, '');
                }

                if ( this.dataset.unavailableVariants != 'show' && option.dataset.available == 'false' && ! ( optionElm.hasAttribute('data-product-url') ) ) {
                    optionElm.setAttribute('disabled', 'disabled');
                } else {
                    if ( ! optionElm.hasAttribute('data-disabled') ) {
                        optionElm.removeAttribute('disabled');
                    }
                }

                if ( option.dataset.available == 'true' ) {
                    if ( ! (optionElm instanceof HTMLOptionElement) ) {
                        optionElm.classList.remove(`${this._prefix}disabled`);
                        // Hidden options (sections/main-product.liquid's hidden_option_names,
                        // eg. Performance Technology driven by the pdp_option_drawer) have no
                        // .product-variant__item wrapper at all - closest() returns null here,
                        // and .classList on that null threw uncaught, silently aborting the
                        // rest of #renderProductUpdates (including the krown:variant:change
                        // dispatch further down) every time a hidden option was part of the
                        // combination. Confirmed live 2026-09-09: this is why a restored
                        // Performance Tech + Accessory pick's price surcharge never re-applied
                        // after the base variant price refreshed.
                        optionElm.closest(`.${this._prefix}product-variant__item`)?.classList.remove(`${this._prefix}disabled`);
                    }
                } else {
                    if ( ! (optionElm.hasAttribute('data-product-url') ) ) {
                        if ( ! (optionElm instanceof HTMLOptionElement) ) {
                            optionElm.classList.add(`${this._prefix}disabled`);
                            optionElm.closest(`.${this._prefix}product-variant__item`)?.classList.add(`${this._prefix}disabled`);
                        }
                    }
                }

            });

        }

        updateBuyButtons(){
            if ( this.productForm ) {
                if ( ! this.currentVariant || ! this.currentVariant.available ) {
                    if ( this.productQty ) this.productQty.style.display = 'none';
                    this.addToCart.classList.add(`${this._prefix}disabled`);
                    this.productForm.classList.add(`${this._prefix}disabled-cart`);
                    this.addToCartText.textContent = KROWN.settings.locales.products_sold_out_variant;
                } else {
                    if ( this.productQty ) this.productQty.style.display = '';
                    this.addToCart.classList.remove(`${this._prefix}disabled`);
                    this.productForm.classList.remove(`${this._prefix}disabled-cart`);
                    this.addToCartText.textContent = this.hasAttribute('data-show-bundle-wording') ? KROWN.settings.locales.products_add_to_bundle_button : this.addToCartText.hasAttribute('data-show-preorder-wording') ? KROWN.settings.locales.products_preorder_button : KROWN.settings.locales.products_add_to_cart_button;
                }
                if ( ! this.currentVariant ) {
                    this.productForm.classList.add(`${this._prefix}unavailable-variant`);
                    this.addToCartText.textContent = (this.variantRequired && this.noVariantSelectedYet) ? KROWN.settings.locales.products_variant_required : KROWN.settings.locales.products_unavailable_variant;
                } else {
                    this.productForm.classList.remove(`${this._prefix}unavailable-variant`);
                }
            }
        }

        #updateCurrentVariant(){
            this.currentVariant = JSON.parse(this.querySelector('[data-js-variant-data]').textContent);
            if ( this.currentVariant == null ) {
                this.currentVariant = false;
            }
        }

        updateStock(productStockData){

            if ( this.productStock ) {

                if ( ! this.currentVariant) {
                    if ( this.productStock ) {
                        this.productStock.innerHTML = '';
                    }
                } else {


                    if ( this.productStock && productStockData[0] ) {

                        let currentVariant = productStockData[0];
                        
                        this.productStock.innerHTML = '';

                        if ( currentVariant ) {

                            if ( currentVariant.quantity <= 0 ) {
                                if ( currentVariant.inventory == 'continue' ) {
                                    this.productStock.innerHTML = KROWN.settings.locales.products_preorder;
                                    this.productStock.setAttribute('data-stock', 'pre-order');
                                } else if ( currentVariant.inventory == 'deny' ) {
                                    this.productStock.innerHTML = KROWN.settings.locales.products_no_products;
                                    this.productStock.setAttribute('data-stock', 'out-of-stock');
                                }
                            } else if ( currentVariant.quantity == '1' ) {
                                this.productStock.innerHTML = KROWN.settings.locales.products_one_product;
                                this.productStock.setAttribute('data-stock', 'one-item-stock');
                            } else if ( currentVariant.quantity <= parseInt(this.productStock.dataset.lowStock) ) {
                                this.productStock.innerHTML = KROWN.settings.locales.products_few_products.replace('{{ count }}', currentVariant.quantity);
                                this.productStock.setAttribute('data-stock', 'little-stock');
                            } else if ( currentVariant.unavailable ) {
                                this.productStock.innerHTML = KROWN.settings.locales.products_no_products;
                                this.productStock.setAttribute('data-stock', 'out-of-stock');
                            } else if ( currentVariant.quantity > parseInt(this.productStock.dataset.lowStock) && this.productStock.dataset.type == "always" ) {
                                this.productStock.innerHTML = KROWN.settings.locales.products_many_products.replace('{{ count }}', currentVariant.quantity);
                                this.productStock.setAttribute('data-stock', 'in-stock');
                            } else if ( ! currentVariant.quantity && this.productStock.dataset.type == "always" )  {
                                this.productStock.innerHTML = KROWN.settings.locales.products_enough_products;
                                this.productStock.setAttribute('data-stock', 'in-stock');
                            }

                            if ( this.productStockProgress ) {
                                let progressQty = 0;
                                if ( currentVariant.quantity <= 0 && currentVariant.inventory == 'continue' || typeof currentVariant.quantity === 'undefined' ) {
                                    progressQty = parseInt(this.productStock.dataset.highStock);
                                } else if ( currentVariant.quantity > 0 ) {
                                    progressQty = currentVariant.quantity;
                                }
                                if ( progressQty >= parseInt(this.productStock.dataset.highStock)  ) {
                                    this.productStockProgress.style.width = `100%`;
                                } else {
                                    this.productStockProgress.style.width = `${100 * progressQty / parseInt(this.productStock.dataset.highStock)}%`;
                                }
                            }

                        }
                    }
                }
            }
        }

        #updateOutOfStockVariants() {

            // Check if all the variants in a group are disabled, show an "out of stock" message

            this.querySelectorAll('[data-js-product-variant]').forEach(variant=>{
                let allDisabled = true;
                variant.classList.remove(`${this._prefix}product-variant--all-disabled`);
                variant.querySelector(`.${this._prefix}product-variant__out-of-stock`)?.classList.add(`${this._prefix}hide`);
                variant.querySelector(`.${this._prefix}product-variant__out-of-stock`)?.setAttribute('aria-hidden', 'true');
                variant.querySelectorAll(`.${this._prefix}product-variant-value`).forEach(variantInput=>{
                    if ( ! ( variantInput.hasAttribute('disabled') || variantInput.classList.contains(`${this._prefix}disabled`) ) ) {
                        allDisabled = false;
                    }
                });
                if ( allDisabled ) {
                    variant.classList.add(`${this._prefix}product-variant--all-disabled`);
                    variant.querySelector(`.${this._prefix}product-variant__out-of-stock`)?.classList.remove(`${this._prefix}hide`);
                    variant.querySelector(`.${this._prefix}product-variant__out-of-stock`)?.removeAttribute('aria-hidden');
                }   
            });

        }

        #updateURL(variantId = '') {
            if (!this.hasAttribute('data-no-history') && variantId !== '') {
                const url = new URL(window.location.href);
                url.searchParams.set('variant', variantId);
                window.history.replaceState({}, '', url);
            }
        }

        #checkIfSelectedVariant(){

            this.options = [];
            this.querySelectorAll('[data-js-product-variant-container]').forEach(elm=>{
                if ( elm.dataset.jsProductVariantContainer == 'radio' ) {
                    elm.querySelectorAll(`.${this._prefix}product-variant__input`).forEach(el=>{
                        if ( el.checked ) {
                            this.options.push(el.value);
                        }
                    });
                } else {
                    if ( ! ( this.variantRequired && elm.selectedIndex == 0 ) ) {
                        this.options.push(elm.value);
                    }
                }
            });
            if ( this.variantRequired && this.noVariantSelectedYet && this.options.length >= parseInt(this.dataset.variants) ) {
                this.noVariantSelectedYet = false;
                this.classList.add(`${this._prefix}variant-selected`);
                this.productPage.classList.add(`${this._prefix}variant-selected`);
            }

        }

    }


  if ( typeof customElements.get('product-variants') == 'undefined' ) {
        customElements.define('product-variants', ProductVariants);
    }

}

/* ---
    Product Form
--- */

if ( typeof ProductForm !== 'function' ) {

    class ProductForm extends HTMLElement {
        constructor() {
            super();   
            this._prefix = window.KT_PREFIX || '';
            this.init();
        }

        init(){
            this.productPage = this.closest('[data-js-product-component]');
            this.cartType = this.hasAttribute('data-ajax-cart') ? ( KROWN.settings.cart_action == 'popup' ? 'popup' : 'ajax' ) : 'page';
            if ( ! document.body.classList.contains('template-cart') || this.hasAttribute('data-force-form' ) ) {
                this.form = this.querySelector('form');
                this.form.addEventListener('submit', this.#onSubmitHandler.bind(this));
                this.ADD_TO_CART = new Event('add-to-cart');
            }
            if ( document.body.classList.contains('touchevents') ) {
                const submitButton = this.querySelector('[type="submit"]');
                if ( submitButton ) {
                    submitButton.addEventListener('touchend', e=>{
                        //submitButton.click();
                    });
                }
            }
        }

        #onSubmitHandler(e) {

            e.preventDefault();
            
            const submitButton = this.querySelector('[type="submit"]');

            submitButton.classList.add(`${this._prefix}working`);

            const body = this.#serializeForm(this.form);
            let alert = '';

            let customPropertiesRequired = false;

            if ( this.productPage ) {
                this.productPage.querySelectorAll('[data-js-custom-property]').forEach(elm=>{
                    if ( elm.querySelector('[required]') ) {

                        if ( elm.classList.contains(`${this._prefix}product-variants--text` ) ) {
                            const txtInputElm = elm.querySelector('input') || elm.querySelector('textarea');
                            if ( txtInputElm.value == '' ) {
                                customPropertiesRequired = true;
                                txtInputElm.focus();
                                elm.querySelector(`.${this._prefix}product-variant__required`).style.display = 'block';
                            }
                        } else if ( elm.classList.contains(`${this._prefix}product-variants--select` ) ) {
                            if ( elm.querySelector('select').value == '' ) {
                                customPropertiesRequired = true;
                                elm.querySelector('select').focus();
                                elm.querySelector(`.${this._prefix}product-variant__required`).style.display = 'block';
                            }
                        } else if ( elm.classList.contains(`${this._prefix}product-variants--checkbox` ) ) {
                            if ( ! elm.querySelector('input:checked') ) {
                                customPropertiesRequired = true;
                                elm.querySelector('input').focus();
                                elm.querySelector(`.${this._prefix}product-variant__required`).style.display = 'block';
                            }
                        }

                        elm.addEventListener('click', ()=>{
                            elm.querySelector(`.${this._prefix}product-variant__required`).style.display = 'none';
                        });

                    }
                });
            }

            if ( customPropertiesRequired ) {
                submitButton.classList.remove(`${this._prefix}working`);
                return false;
            }

            if ( ! customPropertiesRequired ) {

                // One combined /cart/add.js request carrying the base item AND
                // every selected upsell (accessories, plus any Performance Tech
                // pick that isn't already priceCarriedByVariant - see
                // component-pdp-drawers.js) as a single `items` array, instead
                // of two separate sequential POSTs. Shopify's items[] endpoint
                // is atomic: if ANY item in the array is invalid or out of
                // stock, it rejects the whole request and adds NOTHING - so an
                // out-of-stock accessory can no longer leave the shopper with a
                // half-added cart (base item in, accessory silently missing, or
                // vice versa).
                const upsellItems = window.PDPDrawers ? window.PDPDrawers.getSelectedUpsellItems() : [];
                const items = [this.#formBodyToCartItem(body), ...upsellItems];

                fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
                    body: JSON.stringify({ items }),
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    method: 'POST'
                })
                    .then(response => response.json())
                    .then(response => {
                        if ( response.status == 422 ) {
                            // wrong stock logic alert - Shopify's own message already
                            // names whichever specific item (base rower or an upsell)
                            // was rejected, so this one alert covers both cases.
                            alert = document.createElement('span');
                            alert.className = `${this._prefix}alert ${this._prefix}alert--error`;
                            if ( typeof response.description === 'string' ) {
                                alert.innerHTML = response.description;
                            } else {
                                if ( response.errors.send_on ) {
                                    alert.innerHTML = response.errors.send_on;
                                } else {
                                    alert.innerHTML = response.message;
                                }
                            }
                            if ( this.cartType == 'page' || this.cartType == 'popup' ) {
                                if ( document.getElementById('product-page-form-error-cart-alert') ) {
                                    document.getElementById('product-page-form-error-cart-alert').remove();
                                }
                                alert.style.marginTop = '1em';
                                alert.style.marginBottom = '0';
                                alert.id = 'product-page-form-error-cart-alert';
                                this.form.parentElement.append(alert);
                                return false;
                            }
                            return fetch('?section_id=helper-cart');
                        } else {
                            if ( this.cartType == 'page' ) {
                                document.location.href = KROWN.settings.routes.cart_url;
                                return false;
                            } else if ( this.cartType == 'popup' ) {
                                return fetch(`${this.dataset.productUrl}?variant=${this.querySelector('input[name="id"]').value}&section_id=helper-cart-popup`);
                            } else {
                                return fetch('?section_id=helper-cart');
                            }
                        }
                    })
                    .then(response => response.text())
                    .then(text => {

                        const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');

                        if ( this.cartType == 'ajax' ) {
                            
                            const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm').innerHTML;
                            const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal').innerHTML;

                            if ( document.getElementById('AjaxCartForm') ) {
                                const cartItems = document.getElementById('AjaxCartForm');
                                cartItems.innerHTML = cartFormInnerHTML;
                                cartItems.ajaxifyCartItems();
                                if ( alert != '' ) {
                                    document.getElementById('AjaxCartForm').querySelector('form').prepend(alert);
                                }
                                document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalInnerHTML;
                            }

                        } else {
                            if ( document.querySelector('[data-js-site-cart-popup-content]') ) {
                                document.querySelector('[data-js-site-cart-popup-content]').innerHTML = sectionInnerHTML.querySelector('.shopify-section').innerHTML;
                            }
                        }

                        document.querySelectorAll('[data-header-cart-count]').forEach(elm=>{
                            elm.textContent = sectionInnerHTML.querySelector('[data-cart-count]').textContent;
                        });
                        document.querySelectorAll('[data-header-cart-total]').forEach(elm=>{
                            elm.textContent = sectionInnerHTML.querySelector('[data-cart-total]').textContent;
                        });

                        this.dispatchEvent(this.ADD_TO_CART);
                        KROWN.functions.eventDispatcher('krown:cart:add', {
                            productForm: this.querySelector('form'),
                            variantId: this.querySelector('input[name="id"]').value
                        });

                        // a11y
                        if ( document.getElementById('screen-reader-info') ) {
                            document.getElementById('screen-reader-info').innerText = `${KROWN.settings.locales.cart_announcement}`;
                            setTimeout(()=>{
                                document.getElementById('screen-reader-info').innerText = '';
                            }, 1000);
                        }

                    })
                    .catch(e => {
                        console.log(e);
                    })
                    .finally(() => {
                        submitButton.classList.remove(`${this._prefix}working`);
                    });

            }
        }

        #serializeForm(form) {
            let arr = [];
            Array.prototype.slice.call(form.elements).forEach(function(field) {
                if (
                    !field.name ||
                    field.disabled ||
                    ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1
                )
                    return;
                if (field.type === 'select-multiple') {
                    Array.prototype.slice.call(field.options).forEach(function(option) {
                        if (!option.selected) return;
                        arr.push(
                            encodeURIComponent(field.name) +
                                '=' +
                                encodeURIComponent(option.value)
                        );
                    });
                    return;
                }
                if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked)
                    return;
                arr.push(
                    encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value)
                );
            });
            return arr.join('&');
        }

        // Reparses #serializeForm's own url-encoded output into a single
        // /cart/add.js items[] entry, rather than duplicating its whole
        // field-walking logic - properties[Key]=value pairs (custom line item
        // properties, gift card recipient fields) collect into a `properties`
        // object; Shopify form boilerplate (form_type, utf8, return_to, etc.)
        // has no matching case below and is intentionally dropped, since the
        // items[] format doesn't use it.
        #formBodyToCartItem(body) {
            const item = {};
            const properties = {};
            new URLSearchParams(body).forEach((value, name) => {
                const propertyMatch = name.match(/^properties\[(.+)\]$/);
                if (propertyMatch) {
                    properties[propertyMatch[1]] = value;
                } else if (name === 'id') {
                    item.id = value;
                } else if (name === 'quantity') {
                    item.quantity = Number(value) || 1;
                } else if (name === 'selling_plan') {
                    item.selling_plan = value;
                }
            });
            if (Object.keys(properties).length > 0) item.properties = properties;
            if (!item.quantity) item.quantity = 1;
            return item;
        }

    }

  if ( typeof customElements.get('product-form') == 'undefined' ) {
        customElements.define('product-form', ProductForm);
    }

}

/* ---
    Product Recommendations
--- */

if ( typeof ProductRecommendations !== 'function' ) {
    class ProductRecommendations extends HTMLElement {

        constructor() {

            super();  
      
      const SUCCESS = new Event('product-recommendations-loaded');
      const FAIL = new Event('product-recommendations-error');

      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);

        fetch(this.dataset.url)
          .then(response => response.text())
          .then(text => {
            const innerHTML = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector(`product-recommendations`);

                            if ( innerHTML && innerHTML.querySelectorAll(`[data-js-product-item]`).length > 0 ) {
              this.innerHTML = innerHTML.innerHTML;
              this.querySelectorAll('form').forEach(elm=>{
                if (elm.querySelector('template')) {
                  elm.append(elm.querySelector('template').content.cloneNode(true));
                }
              });
              this.dispatchEvent(SUCCESS);
            } else {
                            this.dispatchEvent(FAIL);
                        }
          })
          .catch(e => {
                        this.dispatchEvent(FAIL);
          });
      }

            new IntersectionObserver(handleIntersection.bind(this), {rootMargin: `0px 0px 400px 0px`}).observe(this);

        }

    }
    
  if ( typeof customElements.get('product-recommendations') == 'undefined' ) {
        customElements.define('product-recommendations', ProductRecommendations);
    }

}

/* ---
    Gift card recipent
--- */

if ( typeof GiftCardRecipient !== 'function' ) {

    class GiftCardRecipient extends HTMLElement {

        constructor() {

            super();

            const properties = Array.from(this.querySelectorAll('[name*="properties"]'));
            const checkboxPropertyName = 'properties[__shopify_send_gift_card_to_recipient]';

            this._prefix = window.KT_PREFIX || '';
            this.recipientCheckbox = properties.find(input => input.name === checkboxPropertyName);
            this.recipientOtherProperties = properties.filter(input => input.name !== checkboxPropertyName);
            this.recipientFieldsContainer = this.querySelector(`.${this._prefix}gift-card-recipient__fields`);

            if ( this.recipientCheckbox ) {
                this.recipientCheckbox.addEventListener('change', this.#synchronizeProperties.bind(this));
            }
            this.#synchronizeProperties();

        }

        #synchronizeProperties() {
            this.recipientOtherProperties.forEach(property => property.disabled = !this.recipientCheckbox.checked);
            this.recipientFieldsContainer.classList.toggle(`${this._prefix}hide`, !this.recipientCheckbox.checked);
        }

    }

  if ( typeof customElements.get('gift-card-recipient') == 'undefined' ) {
        customElements.define('gift-card-recipient', GiftCardRecipient);
    }

}

/* ---
    Sticky Add to Cart
--- */

if ( typeof StickyAddToCart !== 'function' ) {
    class StickyAddToCart extends HTMLElement {
        
        constructor() {

            super();  

            const productPage = document.getElementById(this.dataset.id);
            this.hasAttribute('data-append-to') ? document.querySelector(this.dataset.appendTo).appendChild(this) : document.body.appendChild(this);
            
            const productButton = productPage.querySelector('[data-js-product-add-to-cart]');
            const stickyBar = this;

            // sticky_atc_mobile_bottom (sections/main-product.liquid): below 768px the bar
            // docks to the bottom of the screen instead of the top (breakpoint matches
            // section-main-product-pdp.css).
            const mobileBottomQuery = this.classList.contains('sticky-add-to-cart--mobile-bottom')
                ? window.matchMedia('(max-width: 767px)')
                : null;

            // Swatches (bottom-docked mobile bar only). On mobile the buy box's swatch rows
            // (eg. Wood Type) are hidden in favour of a copy here - sections/main-product.liquid
            // marks them .product-variant--in-sticky-bar. The real inputs stay in the buy box
            // and do the work: each swatch here just clicks its counterpart, so a pick goes
            // through the variant picker exactly as a tap there would (a combined-listing wood
            // swatch navigates to that product, carrying ?upsell= across).
            const swatchesContainer = this.querySelector('[data-js-atc-swatches]');
            const swatchRows = swatchesContainer ? productPage.querySelectorAll('.product-variant--in-sticky-bar') : [];
            swatchRows.forEach(row => {
                const inputs = [...row.querySelectorAll('.product-variant__item--color input[type="radio"]')];
                if ( ! inputs.length ) {
                    return;
                }
                const optionName = (row.querySelector('.pdp-option-row__label')?.textContent || '').trim().replace(/:$/, '');

                const group = document.createElement('div');
                group.className = 'sticky-add-to-cart__swatch-group';
                group.setAttribute('role', 'group');
                group.setAttribute('aria-label', optionName);

                const label = document.createElement('div');
                label.className = 'sticky-add-to-cart__swatch-label';
                const labelValue = document.createElement('span');
                labelValue.className = 'sticky-add-to-cart__swatch-value';
                label.append(`${optionName}: `, labelValue);

                const list = document.createElement('div');
                list.className = 'sticky-add-to-cart__swatch-list';
                const swatches = inputs.map(input => {
                    const swatch = document.createElement('button');
                    swatch.type = 'button';
                    swatch.className = 'sticky-add-to-cart__swatch';
                    swatch.setAttribute('aria-label', input.value);
                    // The same image/colour the buy box shows for this value.
                    const visual = input.closest('.product-variant__item')?.querySelector('.product-variant__item-swatch');
                    if ( visual ) {
                        swatch.appendChild(visual.cloneNode(true));
                    }
                    swatch.addEventListener('click', ()=>{
                        if ( ! input.checked ) {
                            input.click();
                        }
                    });
                    list.appendChild(swatch);
                    return { swatch, input };
                });

                // Picks that don't navigate (plain variant swatches) update in place, so
                // keep the selected state and label in step with the real inputs.
                const syncSwatches = () => {
                    const checked = inputs.find(input => input.checked);
                    swatches.forEach(({ swatch, input }) => {
                        swatch.setAttribute('aria-pressed', String(input.checked));
                        swatch.classList.toggle(`${(window.KT_PREFIX || '')}disabled`, input.disabled || input.classList.contains(`${(window.KT_PREFIX || '')}disabled`));
                    });
                    labelValue.textContent = checked ? checked.value : (row.querySelector('.pdp-option-row__value')?.textContent || '').trim();
                };
                syncSwatches();
                row.addEventListener('change', syncSwatches);
                new MutationObserver(syncSwatches).observe(row, {
                    attributes: true, subtree: true, attributeFilter: ['checked', 'disabled', 'class']
                });

                group.append(label, list);
                swatchesContainer.appendChild(group);
            });
            // The only place those swatches show on mobile, so the bar has to stay up.
            const hasSwatches = Boolean(swatchesContainer && swatchesContainer.children.length);
            if ( hasSwatches ) {
                swatchesContainer.hidden = false;
                this.classList.add('sticky-add-to-cart--has-swatches');
            }

            function toggleStickyBarOnScroll() {
                const rect = productButton.getBoundingClientRect();
                const dockedToBottom = Boolean(mobileBottomQuery && mobileBottomQuery.matches);

                // Docked at the top, the bar should show only once the real add-to-cart
                // button has been scrolled past going down (its top edge passes above the
                // viewport), and hide again as soon as you scroll back up past it. The
                // previous mobile-only check instead looked at whether the button was fully
                // inside the viewport, which is also false before you ever reach it — so the
                // bar showed immediately at the top of the page and never cleared on
                // scrolling back up.
                // Docked at the bottom it's meant to be there before the buy box is reached
                // too: show it whenever the real button is fully off screen in either
                // direction, and get out of the way while that button is on screen so the
                // two never show at once - unless it's carrying the swatches, which have
                // nowhere else to be on mobile. Either way it stays hidden at the very top
                // of the page (roughly the header's height, ~100px on mobile), so the page
                // opens without it and it tucks away again on scrolling back up there.
                const atTopOfPage = window.scrollY < 100;
                const showBar = dockedToBottom
                    ? ! atTopOfPage && ( hasSwatches || rect.bottom <= 0 || rect.top >= window.innerHeight )
                    : rect.top < 0;

                stickyBar.classList.toggle(`${(window.KT_PREFIX || '')}visible`, showBar);
                // Lets page-level CSS make room for the bar while it's up (the chat
                // widget's launcher would otherwise sit on top of its button).
                document.documentElement.classList.toggle('sticky-atc-bottom-visible', dockedToBottom && showBar);
            }
            window.addEventListener('scroll', toggleStickyBarOnScroll, { passive: true });
            if ( mobileBottomQuery ) {
                mobileBottomQuery.addEventListener('change', toggleStickyBarOnScroll);
                // Page-level CSS that has to clear the bottom-docked bar (the chat
                // launcher, the footer) reads its height from here, since that varies
                // (swatches or not, a sale price wrapping onto two lines). The 10px is
                // the gap the bar floats above the bottom edge.
                const setBottomClearance = ()=>{
                    document.documentElement.style.setProperty('--sticky-atc-bottom-clearance', `${Math.ceil(this.offsetHeight) + 10}px`);
                };
                setBottomClearance();
                new ResizeObserver(setBottomClearance).observe(this);
            }
            toggleStickyBarOnScroll();

            // Only rendered for simple_purchase products, or for the bottom-docked mobile
            // bar - the top bar's usual CTA is a plain "Configure Rower" link to
            // #product-information instead. Wired up outside the data-single branch below
            // so it works whatever sticky_atc_variants is set to: it only ever drives the
            // buy box's own button, so whatever variant is selected there is what's added.
            const stickyButton = this.querySelector('button[data-js-atc]');
            if ( stickyButton ) {
                const stickyButtonText = stickyButton.querySelector('[data-js-atc-text]');
                const productButtonText = productButton.querySelector('[data-js-product-add-to-cart-text]');

                stickyButton.addEventListener('click', ()=>{
                    // The buy box button is disabled while the selected variant is sold out
                    // or unavailable (or not chosen yet) - take the shopper to it so they can
                    // see why, instead of submitting an add that can't succeed.
                    if ( productButton.classList.contains(`${(window.KT_PREFIX || '')}disabled`) ) {
                        productButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                    productButton.click();
                    stickyButton.classList.add(`${(window.KT_PREFIX || '')}working`);
                });

                // Mirrors the buy box button's loading/disabled state and its label, which
                // ProductVariants.updateBuyButtons() swaps between add to cart, sold out and
                // unavailable as the selected variant changes.
                const syncStickyButton = () => {
                    ['working', 'disabled'].forEach(state => {
                        const stateClass = `${(window.KT_PREFIX || '')}${state}`;
                        stickyButton.classList.toggle(stateClass, productButton.classList.contains(stateClass));
                    });
                    if ( stickyButtonText && productButtonText ) {
                        stickyButtonText.textContent = productButtonText.textContent.trim();
                    }
                };
                syncStickyButton();
                new MutationObserver(syncStickyButton).observe(productButton, {
                    attributes: true, attributeFilter: ['class'], childList: true, subtree: true, characterData: true
                });
            }

            if ( this.hasAttribute('data-single') ) {

                const productPage = document.querySelector(`#${this.dataset.id}`);

                if ( productPage.classList.contains(`${(window.KT_PREFIX || '')}variant-selected`) ) {
                    this.classList.add(`${(window.KT_PREFIX || '')}variant-selected`);
                } else {
                    new MutationObserver(()=>{
                        if ( productPage.classList.contains(`${(window.KT_PREFIX || '')}variant-selected`) ) {
                            this.classList.add(`${(window.KT_PREFIX || '')}variant-selected`);
                        } else {
                            this.classList.remove(`${(window.KT_PREFIX || '')}variant-selected`);
                        }
                    }).observe(productPage, {
                        attributes: true, childList: false, subtree: false
                    });
                }

                // when there are multiple variants

                if ( this.hasAttribute('data-single-with-options') ) {
                    const stickyPrice = this.querySelector('[data-js-atc-price]');
                    const productPrice = document.querySelector(`#${this.dataset.id} [data-update-block="price-sticky-main"]`);
                    if ( stickyPrice !== null && productPrice !== null ) {
                        stickyPrice.innerHTML = productPrice.innerHTML;
                        new MutationObserver(()=>{
                            stickyPrice.innerHTML = productPrice.innerHTML;
                        }).observe(productPrice, {
                            attributes: false, childList: true, subtree: true, characterData: true
                        })
                    }
                }

            } 

            this.querySelector('button[data-js-choose]')?.addEventListener('click', ()=>{
                productPage.querySelector('product-variants').scrollIntoView({behavior: "smooth", block: "center"});
            });

        }

    }

  if ( typeof customElements.get('sticky-add-to-cart') == 'undefined' ) {
        customElements.define('sticky-add-to-cart', StickyAddToCart);
    }

}

/* ---
    PDP Wishlist Button
--- */

// Extra wishlist hearts on the product page (bottom-docked mobile sticky bar, mobile
// title row - snippets/pdp-wishlist-button.liquid). Wishlist Plus (Swym) only drives the
// single button it injects into #swym-custom-wishlist-button (its "custom" button
// position, in the breadcrumb back-link row), so each of these is a stand-in for that
// button: hidden until Swym has injected it, a tap clicks it, and it copies that
// button's icon. Swym swaps the icon between its outline and filled templates as the
// product is saved/removed - in its pre-consent local wishlist mode too, where it sets
// no aria-pressed - so the icon is the one state signal that's always there.
if ( typeof PdpWishlistButton !== 'function' ) {
    class PdpWishlistButton extends HTMLElement {

        constructor() {

            super();

            this.swymSlot = document.getElementById('swym-custom-wishlist-button');
            this.button = this.querySelector('button');
            if ( ! this.swymSlot || ! this.button ) {
                return;
            }
            this.icon = this.querySelector('[data-js-wishlist-icon]');
            this.defaultLabel = this.button.getAttribute('aria-label');

            // Swym's handler reads e.target.closest('button'), so click its button
            // itself rather than the container the listener is bound to.
            this.button.addEventListener('click', ()=>{
                const swymButton = this.getSwymButton();
                if ( swymButton ) {
                    swymButton.click();
                }
            });

            this.sync();
            new MutationObserver(this.sync.bind(this)).observe(this.swymSlot, {
                attributes: true, childList: true, subtree: true
            });

        }

        getSwymButton() {
            return this.swymSlot.querySelector('.swym-button-content button, .swym-button-content [role="button"]');
        }

        sync() {
            const swymButton = this.getSwymButton();
            this.hidden = ! swymButton;
            if ( ! swymButton ) {
                return;
            }
            const swymIcon = swymButton.querySelector('svg');
            if ( swymIcon && this.icon ) {
                this.icon.replaceChildren(swymIcon.cloneNode(true));
            }
            // Disabled while Swym is mid-request.
            this.button.disabled = Boolean(swymButton.disabled);
            if ( swymButton.hasAttribute('aria-pressed') ) {
                this.button.setAttribute('aria-pressed', swymButton.getAttribute('aria-pressed'));
            } else {
                this.button.removeAttribute('aria-pressed');
            }
            this.button.setAttribute('aria-label', swymButton.getAttribute('aria-label') || this.defaultLabel);
        }

    }

  if ( typeof customElements.get('pdp-wishlist-button') == 'undefined' ) {
        customElements.define('pdp-wishlist-button', PdpWishlistButton);
    }

}

/* ---
    Pickup Availability Widget
--- */

if ( typeof PickupAvailabilityWidget !== 'function' ) {
    class PickupAvailabilityWidget extends HTMLElement {

        constructor(){

            super();

            this._prefix = window.KT_PREFIX || '';
            
            if ( this.querySelector('template') ) {

                const pickupSidebar = this.querySelector('template').content.firstElementChild.cloneNode(true);
                pickupSidebar.id = `site-availability-sidebar-${this.dataset.id}`;

                if ( document.querySelector(`#site-availability-sidebar-${this.dataset.id}`) ) {
                    document.querySelector(`#site-availability-sidebar-${this.dataset.id}`).remove();
                }

                document.body.append(pickupSidebar);

                const openSidebarButton = this.querySelector(`.${this._prefix}pickup-availability-widget__more`);

                if ( openSidebarButton ) {
                    openSidebarButton.setAttribute('aria-controls', pickupSidebar.id);
                    openSidebarButton.setAttribute('aria-expanded', 'false');
                    this.querySelector('[data-js-open-site-availability-sidebar]').addEventListener('keydown', e=>{
                        if ( e.keyCode == window.KEYCODES.RETURN ) {
                            window.lastFocusedElm = this.querySelector('[data-js-open-site-availability-sidebar]');
                            setTimeout(()=>{
                                pickupSidebar.querySelector('[data-js-close]').focus();
                            }, 50);
                        }
                    });
                    this.querySelector('[data-js-open-site-availability-sidebar]').addEventListener('click', e=>{
                        pickupSidebar.show();
                        openSidebarButton.setAttribute('aria-expanded', 'true');
                    });
                }

                const observer = new MutationObserver(()=>{
                    pickupSidebar.remove();
                    observer.disconnect();
                });
                observer.observe(this.parentElement, {
                    attributes: false, childList: true, subtree: false
                });

            }

            this.querySelectorAll(`.${this._prefix}pickup-availability-widget__location-view`).forEach(elm=>{
                elm.addEventListener('click', ()=>{
                    document.getElementById(`${elm.getAttribute('aria-controls')}`)?.classList.toggle(`${this._prefix}opened`);
                    elm.setAttribute('aria-selected', elm.getAttribute('aria-selected') == "true" ? "false" : "true");
                })
            })

        }

    }

  if ( typeof customElements.get('pickup-availability-widget') == 'undefined' ) {
        customElements.define('pickup-availability-widget', PickupAvailabilityWidget);
    }

}

/* ---
    Product Policies
--- */

if ( typeof ProductPolicies !== 'function' ) {

    class ProductPolicies extends HTMLElement {

        constructor() {

            super();

            this._linkBehavior = this.dataset.policyLink || 'self';
            this._modal = this.querySelector('modal-box') || ( this.dataset.policyModalId ? document.getElementById(this.dataset.policyModalId) : null );

            if ( this._modal && this._modal.parentElement !== document.body ) {
                document.body.appendChild(this._modal);
            }

            this.querySelectorAll('a').forEach(link => {

                switch ( this._linkBehavior ) {

                    case 'popup':
                        if ( this._modal ) {
                            link.addEventListener('click', e => {
                                e.preventDefault();
                                this._modal.show();
                            });
                        }
                        break;

                    case 'blank':
                        link.setAttribute('target', '_blank');
                        link.setAttribute('rel', 'noopener');
                        break;

                }

            });

        }

    }

    if ( typeof customElements.get('product-policies') == 'undefined' ) {
        customElements.define('product-policies', ProductPolicies);
    }

}

/* ---
    Coupon Code
--- */
if ( typeof CouponCode !== 'function' ) {
    class CouponCode extends HTMLElement {
        constructor() {
            super();

            this.querySelectorAll('[data-copy-coupon]').forEach(btn => {
                btn.addEventListener('click', () => this.#copy(btn));

                btn.addEventListener('keydown', event => {
                    if ( event.key === 'Enter' || event.key === ' ' ) {
                        event.preventDefault();
                        this.#copy(btn);
                    }
                });
            });
        }

        #copy(btn) {
            const code = btn.closest('.product-coupon-code__code').querySelector('[data-coupon-code]').textContent.trim();

            if ( navigator.clipboard && window.isSecureContext ) {
                navigator.clipboard.writeText(code).then(() => this.#confirm(btn)).catch(() => this.#fallbackCopy(code, btn));
            } else {
                this.#fallbackCopy(code, btn);
            }
        }

        #fallbackCopy(code, btn) {
            const el = document.createElement('textarea');
            el.value = code;
            el.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            this.#confirm(btn);
        }

        #confirm(btn) {
            btn.classList.add('copied');
            setTimeout(() => btn.classList.remove('copied'), 2000);
        }
    }

    if ( typeof customElements.get('coupon-code') == 'undefined' ) {
        customElements.define('coupon-code', CouponCode);
    }
}
